import type { QueryClient } from '@tanstack/react-query';
import { clearWorkspaceQueries } from './workspace-clear';

/**
 * Equivalent to the Ember app's `environment/route.ts beforeModel +
 * unloadModels` flow, adapted to Hydra's route lifecycle.
 *
 * Mounted at the authenticated workspace route boundary.
 * `handleWorkspaceTransition` runs when the URL's `/workspaces/:id/...`
 * segment changes, then drops the previous workspace's cache slots so
 * a deep navigation into the new workspace can't read stale data.
 *
 * The cache clear is synchronous (`qc.removeQueries` is instant), so
 * the route boundary returns immediately and doesn't enter a pending
 * navigation state. Account-scoped (`['account', ...]`) and global
 * (`[entity, ...]`) caches are untouched, matching the Ember
 * `modelsToKeep` reseed convention.
 *
 * Idempotent on first call (no previous workspace recorded yet).
 *
 * Module-scope state implications:
 *   - Per-tab: each tab gets its own module instance, so production is fine.
 *   - Tests: `src/test/setup.ts` calls `__resetWorkspaceTransitionForTests`
 *     in `afterEach` so the cursor doesn't leak across files.
 *   - HMR: Vite preserves module state across hot reloads. After editing
 *     this file in dev, the next workspace switch may be a no-op because
 *     the cursor still points at the current id. Trigger a full reload if
 *     the dev cache feels stale.
 */

let previousWorkspaceId: number | null = null;

export function handleWorkspaceTransition(qc: QueryClient, nextWorkspaceId: number | null): void {
  if (previousWorkspaceId != null && nextWorkspaceId !== previousWorkspaceId) {
    clearWorkspaceQueries(qc, previousWorkspaceId);
  }
  previousWorkspaceId = nextWorkspaceId;
}

/** Test-only seam to reset the module-scope cursor between cases. */
export function __resetWorkspaceTransitionForTests(): void {
  previousWorkspaceId = null;
}
